import type { ItemAction } from './resource-list-view-types';

/**
 * The shared vocabulary for resource menus — one place that decides what an
 * action is called, which glyph it draws, and whether it reads as destructive.
 *
 * Every list in the app ends its kebab menu with the same trio (Share, Move to
 * folder, Archive), and before this each page spelled out its own copy of all
 * three. Nine copies had already drifted once: the chat page's Rename drew
 * `pencil` while the dataset page's drew `pencil-square` for the same operation.
 * So the label/glyph/variant pairing lives here and call sites pass only the
 * handler.
 *
 * What does NOT belong here is anything a single page offers — Add workflow,
 * View runs, Set emoji. A builder with one caller is just indirection; those
 * stay written out at their call site.
 */
export const ResourceMenuItem = {
  /**
   * Opens a resource, leaving the list behind — hence the arrow-out-of-a-box
   * glyph. Folders use {@link ResourceMenuItem.openFolder} instead, since
   * opening one descends into it rather than navigating away.
   */
  open: (onClick: () => void): ItemAction => ({
    label: 'Open',
    icon: 'arrow-top-right-on-square',
    onClick,
  }),

  openFolder: (onClick: () => void): ItemAction => ({
    label: 'Open',
    icon: 'folder-open',
    onClick,
  }),

  /**
   * Retitles the row in place. `label` is a parameter because the noun varies
   * ('Rename folder', 'Rename workflow') even though the operation does not —
   * the glyph must not vary with it. Distinct from an Edit entry that opens an
   * editor, which draws the pencil-on-a-page `pencil-square`.
   */
  rename: (onClick: () => void, label = 'Rename'): ItemAction => ({
    label,
    icon: 'pencil',
    onClick,
  }),

  /**
   * Stars or unstars the row for the current user. One entry rather than two,
   * because a row is either favorited or it is not and the menu should not
   * offer the branch that does nothing — the label carries the direction.
   *
   * First in every menu that carries it. Favoriting is the one entry here that
   * changes nothing for anyone else — no grant moves, nothing is renamed or
   * filed — so it leads, above the actions that do, with the destructive one
   * furthest from it. Notion and Linear both open their row menus this way.
   */
  favorite: (isFavorite: boolean, onClick: () => void): ItemAction => ({
    label: isFavorite ? 'Unfavorite' : 'Favorite',
    // The glyph turns over with the label rather than staying a plain star for
    // both: a menu row is read glyph-first, and one star for "add" and "remove"
    // left the icon saying nothing about which way the click goes.
    icon: isFavorite ? 'star-off' : 'star',
    onClick,
  }),

  share: (onClick: () => void): ItemAction => ({
    label: 'Share',
    icon: 'share',
    onClick,
  }),

  moveToFolder: (onClick: () => void): ItemAction => ({
    label: 'Move to folder',
    icon: 'folder',
    onClick,
  }),

  /**
   * Archiving is the destructive entry in every one of these menus — it is the
   * only reason `variant` is set anywhere in the list menus, and setting it here
   * is what keeps a page from shipping a black Archive row.
   */
  archive: (onClick: () => void): ItemAction => ({
    label: 'Archive',
    icon: 'archive-box',
    variant: 'destructive',
    onClick,
  }),
} as const;

/**
 * The trailing trio every resource menu ends with, in the order they always
 * appear: the two harmless placements first, the destructive one last and
 * separated from the rest by distance rather than by a rule.
 *
 * Menus that interleave something else (the workflow row slots Rename between
 * Share and Move) compose from {@link ResourceMenuItem} directly instead.
 */
export const shareMoveArchiveItems = ({
  onShare,
  onMoveToFolder,
  onArchive,
}: {
  onShare: () => void;
  onMoveToFolder: () => void;
  onArchive: () => void;
}): ItemAction[] => [
  ResourceMenuItem.share(onShare),
  ResourceMenuItem.moveToFolder(onMoveToFolder),
  ResourceMenuItem.archive(onArchive),
];
